import { useCallback, useState } from 'react'
import type { OnMoveEnd, Viewport } from '@xyflow/react'
import { readCanvasViewport, writeCanvasViewport } from '../utils/canvasViewport'

export interface CanvasViewportHandle {
  /**
   * Der Ausschnitt beim Aufbau. Einmal gelesen und danach unveränderlich —
   * React Flow liest `defaultViewport` ohnehin nur beim Aufbau von `XYPanZoom`
   * (`@xyflow/react`, `index.js:1318`), eine spätere Änderung käme nicht an.
   */
  initialViewport: Viewport
  /** Merkt sich den fertigen Ausschnitt. Direkt als `onMoveEnd` verwendbar. */
  rememberViewport: OnMoveEnd
}

/**
 * Merkt sich den **Ausschnitt** eines Projekt-Canvas (siehe CONTEXT.md) und
 * gibt ihn beim nächsten Öffnen zurück.
 *
 * Der Canvas wird beim Schließen abgebaut und beim Öffnen neu aufgebaut — der
 * gemerkte Ausschnitt ist deshalb schlicht der Startwert des nächsten Aufbaus,
 * es braucht keinen Effekt, der ihn nachträglich setzt.
 *
 * Gemerkt wird bei `onMoveEnd`, also nach jeder abgeschlossenen Geste. Ein
 * eigenes Entprellen wäre doppelte Arbeit: React Flow wartet beim Wischen
 * selbst 150 ms ab (`createPanZoomEndHandler` in `@xyflow/system`). Auch ⇧0
 * und der Doppelklick aufs Hand-Werkzeug laufen dort durch — sie gehen über
 * dieselbe d3-Zoom-Instanz und lösen das Ende-Ereignis mit aus.
 *
 * @example
 * const { initialViewport, rememberViewport } = useCanvasViewport(projectId)
 * <ReactFlow defaultViewport={initialViewport} onMoveEnd={rememberViewport} />
 */
export function useCanvasViewport(projectId: string): CanvasViewportHandle {
  // Im Anfangswert, nicht im Rumpf: sonst liefe der Zugriff auf den Speicher
  // bei jedem Rendern, und das sind während einer Zieh-Geste viele.
  const [initialViewport] = useState<Viewport>(() => readCanvasViewport(projectId))

  const rememberViewport = useCallback<OnMoveEnd>((_event, viewport) => {
    writeCanvasViewport(projectId, viewport)
  }, [projectId])

  return { initialViewport, rememberViewport }
}
